import { ReactNode, FC, useRef } from "react";
import { css, cx } from "@/domains/emotion";
import { EmotionClassStyles } from "@/domains/emotion/types";
import { MdsDropdownContentList } from "@/design-system/components/dropdown";
import { MdsDropdownContent } from "@/design-system/components/dropdown/MdsDropdownContent";
import { Popover } from "react-aria-components";

type MdsPlacement =
  | "above"
  | "above-right-alignment"
  | "below"
  | "below-center"
  | "below-right-alignment"
  | "right";

export interface MdsPopoverDropdownProps extends EmotionClassStyles {
  isOpen: boolean;
  onOpenChange: (value: boolean) => void;
  contentList: MdsDropdownContentList;
  children: ReactNode;
  placement?: MdsPlacement;
  offset?: number;
  triggerRef?: React.RefObject<HTMLElement>;
  innerStyles?: {
    ContentContainer?: {
      className?: string;
    };
    Content?: {
      className?: string;
    };
    Row?: {
      className?: string;
    };
  };
}

const contentContainerStyles = css({
  position: "relative",
});

const rightAlignContainerStyles = css({
  position: "relative",
  height: "fit-content",
});

/**
 * A dropdown component that uses react-aria-components Popover instead of FloatingPortal.
 * This provides better accessibility and follows the MDS design system patterns.
 *
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsPopoverDropdown: FC<MdsPopoverDropdownProps> = ({
  className,
  children,
  isOpen,
  onOpenChange,
  contentList,
  placement,
  innerStyles,
  offset: offsetProp,
  triggerRef,
}) => {
  const internalTriggerRef = useRef<HTMLDivElement>(null);
  const ref = (triggerRef as React.RefObject<HTMLDivElement>) || internalTriggerRef;

  const placementContainerStyles =
    placement === "below-right-alignment" || placement === "above-right-alignment"
      ? rightAlignContainerStyles
      : undefined;
  const placedContentContainerStyles = cx(contentContainerStyles, placementContainerStyles);

  return (
    <>
      <div ref={ref} className={className}>
        {children}
      </div>
      <Popover
        triggerRef={ref}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        offset={offsetProp ?? 4}
        isNonModal
      >
        <div className={cx(placedContentContainerStyles, innerStyles?.ContentContainer?.className)}>
          <MdsDropdownContent
            className={innerStyles?.Content?.className}
            onOpenChange={onOpenChange}
            contentList={contentList}
            rowStyles={innerStyles?.Row?.className}
          />
        </div>
      </Popover>
    </>
  );
};
